import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Card, Form, Row, Col } from 'antd';
import PropTypes from 'prop-types';

import Heading from 'components/Heading/Heading';
import Button from 'components/Button/Button';
import { getPropertyTypes } from 'utils/apis/constants';
import { useFetchConstant } from 'utils/hooks/general';

import PropertyCard from '../../components/PropertyCard/PropertyCard';
import PropertyInfo from '../../components/PropertyInfo/PropertyInfo';
import PropertyAdd from '../../components/PropertyAdd/PropertyAdd';

const StyledContainer = styled(Col)`
  padding: 16px;
`;

const PropertyRegistration = ({ values, className, propertyName, propertyAddress, onBack, onSave, isNextLoading, ...props }) => {
  const [properties, setProperties] = useState(values);
  const [isModalVisible, setModalVisibility] = useState(false);
  const [editProperty, setEditProperty] = useState({});
  const [editPropertyIndex, setEditPropertyIndex] = useState(-1);
  const [propertyTypes, setPropertyTypes] = useState({});

  const propertyIds = properties.filter(property => property._id).map(property => property._id);
  const { selections: countrySelections } = useFetchConstant('countries', 'Error while getting countries.', 'iso2', 'name');
  const { selections: stateSelections } = useFetchConstant('statesMY');

  useEffect(() => {
    getPropertyTypes().then(res => setPropertyTypes(res.data))
  }, [])

  const handleOnPropertySave = newProperty => {
    const country = countrySelections.find((countrySelection) => countrySelection.value === newProperty.propertyCountry);
    const state = stateSelections.find((stateSelection) => stateSelection.value === newProperty.propertyState);
    const propertyPayload = {
      name: newProperty.propertyName,
      address: `${newProperty.propertyAddress}, ${newProperty.propertyCity}, ${country ? `${country.label},` : ''} ${newProperty.propertyZipCode}${state ? `, ${state.label}` : ''}`,
      streetAddress: newProperty.propertyAddress,
      city: newProperty.propertyCity,
      state: newProperty.propertyState,
      zipcode: newProperty.propertyZipCode,
      country: newProperty.propertyCountry,
      type: newProperty.propertyType,
      totalNoOfUnits: newProperty.totalNoOfUnits,
      isHostSearchProperty: newProperty.isHostSearchProperty,
      latitude: newProperty.latitude,
      longitude: newProperty.longitude
    };

    if (editPropertyIndex > -1) {
      properties[editPropertyIndex] = propertyPayload;
      setProperties([...properties]);
      setEditProperty({});
      setEditPropertyIndex(-1);
    } else {
      setProperties([
        ...properties,
        {
          _id: newProperty.isHostSearchProperty ? newProperty.propertyId : undefined,
          ...propertyPayload,
        }
      ]);
    }
  };

  const handleOnPropertyDelete = index => () => {
    properties.splice(index, 1);
    setProperties([...properties]);
  };

  const handleOnBack = e => {
    e.preventDefault();
    onBack(properties);
  };

  const handleOnComplete = e => {
    onSave(properties);
  };

  const handleOnAddProperty = () => {
    setEditPropertyIndex(-1);
    setModalVisibility(true);
  };

  const handleOnModalClose = () => {
    setEditProperty({});
    setEditPropertyIndex(-1);
    setModalVisibility(false);
  };

  const handleOnPropertyEdit = index => () => {
    setModalVisibility(true);
    setEditPropertyIndex(index);
    setEditProperty(properties[index]);
  };

  return (
    <Form className={className}>
      <Row>
        <Heading style={{ textAlign: 'center' }} size="sub">
          Which properties do you host?
        </Heading>
      </Row>
      <Row>
        <Card title="Property List">
          <Row type="flex">
            {properties.map((property, index) => (
              <StyledContainer key={index} span={24} md={8}>
                <PropertyCard
                  shouldHideEdit={property.isHostSearchProperty}
                  listAllPropertyTypes={propertyTypes}
                  propertyName={property.name}
                  propertyAddress={property.address}
                  propertyType={property.type}
                  onDeleteClick={handleOnPropertyDelete(index)}
                  onEditClick={handleOnPropertyEdit(index)}
                />
              </StyledContainer>
            ))}
            <StyledContainer span={24} md={8}>
              <PropertyAdd onClick={handleOnAddProperty} />
            </StyledContainer>
            <PropertyInfo
              onPropertySave={handleOnPropertySave}
              visible={isModalVisible}
              onModalClose={handleOnModalClose}
              defaultValue={editProperty}
              editProperty={editProperty > -1 ? editPropertyIndex : undefined}
              selectedBuildings={propertyIds}
            />
          </Row>
        </Card>
      </Row>
      <Row type="flex" justify="space-between">
        <Col span={6}>
          <Button onClick={handleOnBack} size="large" style={{ width: '100%', marginTop: '48px' }} type="secondary">
            Back
          </Button>
        </Col>
        <Col span={6}>
          <Button loading={isNextLoading} size="large" onClick={handleOnComplete} style={{ width: '100%', marginTop: '48px' }} type="primary">
            Next
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

PropertyRegistration.propTypes = {
  props: PropTypes.object,
  onSave: PropTypes.func,
  onBack: PropTypes.func,
  values: PropTypes.array
};

PropertyRegistration.defaultProps = {
  history: {},
  values: []
};

export default styled(PropertyRegistration)`
  padding: 8% 4% !important;
  width: 100%;

  @media (min-width: 992px) {
    padding: 4% 8% !important;
    width: 68%;
  }
`;
